/**
 * Finance — daily close, sign-off and data exports (live).
 */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { PageHead } from "../components/PageHead";
import { ErrorCard, LoadingCard } from "../components/Async";
import { useAdminQuery, downloadCsv, shortDate, tzs, errorMessage } from "../lib/admin";
import { adminApi } from "../lib/api";
import type { DailyClose, SalesReport } from "../lib/types";
import { Calendar, CheckCircle2, Download, AlertTriangle, FileText } from "lucide-react";

interface ExportEntry {
  name: string;
  format: string;
  endpoint: string;
}

export function Finance() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [signedOff, setSignedOff] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const close = useAdminQuery<DailyClose>(["finance-close", date], "/admin/v1/finance/daily-close", { date });
  const week = useAdminQuery<SalesReport>(["finance-week"], "/admin/v1/reports/sales", { period: "7d" });
  const exportsList = useAdminQuery<{ exports: ExportEntry[] }>(["finance-exports"], "/admin/v1/finance/exports");

  const signOff = useMutation({
    mutationFn: (day: string) => adminApi.post(`/admin/v1/finance/daily-close/${day}/sign-off`),
    onSuccess: (_, day) => { setSignedOff(day); setActionError(null); },
    onError: (error) => setActionError(errorMessage(error)),
  });

  const cell = (value: unknown): string | number =>
    typeof value === "string" || typeof value === "number" ? value : "";

  const exportReport = async (entry: ExportEntry) => {
    try {
      const payload = await adminApi.get<Record<string, unknown>>(entry.endpoint);
      if (entry.name === "Agent summary") {
        const rows = (payload.data ?? []) as Array<Record<string, unknown>>;
        downloadCsv("evocha-agents-report.csv",
          ["Code", "Business", "Region", "Status", "KYC", "Orders", "Gross", "Commission"],
          rows.map((r) => [cell(r.code), cell(r.business_name), cell(r.region), cell(r.status), cell(r.kyc_status), cell(r.orders), Number(r.gross_minor ?? 0) / 100, Number(r.commission_minor ?? 0) / 100]),
        );
      } else if (entry.name === "Commission") {
        const rows = (payload.agents ?? []) as Array<Record<string, unknown>>;
        downloadCsv("evocha-commission-report.csv",
          ["Code", "Business", "Commission"],
          rows.map((r) => [cell(r.code), cell(r.business_name), Number(r.commission_minor ?? 0) / 100]),
        );
      } else if (entry.name === "Float & exposure") {
        const rows = (payload.wallets ?? []) as Array<Record<string, unknown>>;
        downloadCsv("evocha-float-report.csv",
          ["Wallet", "Agent", "Available", "Reserved", "Commission"],
          rows.map((r) => {
            const agent = r.agent as { business_name?: string } | null;
            return [cell(r.wallet_id), cell(agent?.business_name), Number(r.available_minor ?? 0) / 100, Number(r.reserved_minor ?? 0) / 100, Number(r.commission_minor ?? 0) / 100];
          }),
        );
      } else {
        const rows = (payload.daily ?? []) as Array<Record<string, unknown>>;
        downloadCsv("evocha-sales-report.csv",
          ["Date", "Orders", "Gross", "Commission"],
          rows.map((r) => [cell(r.date), cell(r.orders), Number(r.gross_minor ?? 0) / 100, Number(r.commission_minor ?? 0) / 100]),
        );
      }
      setActionError(null);
    } catch (error) {
      setActionError(errorMessage(error));
    }
  };

  const data = close.data;

  return (
    <>
      <PageHead
        title="Finance"
        description="Daily close, sign-off, and report exports."
        actions={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input className="ev-input" type="date" value={date} onChange={(e) => { setDate(e.target.value); setSignedOff(null); }} aria-label="Close date" />
            <button
              className="ev-btn ev-btn-sm ev-btn-primary"
              disabled={signOff.isPending || signedOff === date}
              onClick={() => signOff.mutate(date)}
            >
              <CheckCircle2 size={16} /> {signedOff === date ? "Signed off" : "Sign off"}
            </button>
          </div>
        }
      />

      {actionError && (
        <div className="admin-note" style={{ marginBottom: 12 }}>
          <AlertTriangle size={16} />
          <span>{actionError}</span>
        </div>
      )}

      {close.isLoading && <LoadingCard label="Loading daily close…" />}
      {close.error && <ErrorCard error={close.error} onRetry={() => void close.refetch()} />}

      {data && (
        <div className="admin-kpi-grid" style={{ marginBottom: 24 }}>
          <div className="admin-card">
            <span className="ev-body-muted">GMV ({data.date})</span>
            <div className="ev-display-s" style={{ color: "var(--ev-mint-600)" }}>{tzs(data.gmv_minor)}</div>
            <span className="ev-body-micro muted">{data.success} of {data.orders} orders successful</span>
          </div>
          <div className="admin-card">
            <span className="ev-body-muted">Commission</span>
            <div className="ev-display-s" style={{ color: "var(--ev-blue-600)" }}>{tzs(data.commission_minor)}</div>
            <span className="ev-body-micro muted">Provider cost {tzs(data.provider_cost_minor)}</span>
          </div>
          <div className="admin-card">
            <span className="ev-body-muted">Funding credited</span>
            <div className="ev-display-s">{tzs(data.funding_credited_minor)}</div>
            <span className="ev-body-micro muted">{data.funding_count} top-ups</span>
          </div>
          <div className="admin-card">
            <span className="ev-body-muted">Net cash</span>
            <div className="ev-display-s" style={{ color: data.net_cash_minor >= 0 ? "var(--ev-mint-600)" : "var(--ev-red-600)" }}>
              {tzs(data.net_cash_minor)}
            </div>
            <span className="ev-body-micro muted">
              {data.pending} pending · {data.review} review · {data.failed} failed
            </span>
          </div>
        </div>
      )}

      <div className="admin-row-6" style={{ marginBottom: 24 }}>
        <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="admin-card-header" style={{ padding: "16px 20px", marginBottom: 0 }}>
            <h3>Last 7 days</h3>
          </div>
          {week.isLoading && <div style={{ padding: 20 }}><LoadingCard label="Loading sales…" /></div>}
          {week.error && <div style={{ padding: 20 }}><ErrorCard error={week.error} /></div>}
          {week.data && (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th className="admin-table-th">Date</th>
                    <th className="admin-table-th">Orders</th>
                    <th className="admin-table-th">Gross</th>
                    <th className="admin-table-th">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {week.data.daily.map((d) => (
                    <tr key={d.date} className="admin-table-row-hover">
                      <td className="admin-table-td" data-label="Date">
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Calendar size={14} style={{ color: "var(--ev-slate-400)" }} />
                          <span className="ev-body">{shortDate(d.date)}</span>
                        </div>
                      </td>
                      <td className="admin-table-td" data-label="Orders">{d.orders}</td>
                      <td className="admin-table-td" data-label="Gross"><span className="ev-body-mono ev-body-semibold">{tzs(d.gross_minor)}</span></td>
                      <td className="admin-table-td" data-label="Commission"><span className="ev-body-mono" style={{ color: "var(--ev-blue-600)" }}>{tzs(d.commission_minor)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="admin-card">
          <h3>Exports</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
            {exportsList.isLoading && <LoadingCard label="Loading exports…" />}
            {exportsList.error && <ErrorCard error={exportsList.error} />}
            {(exportsList.data?.exports ?? []).map((entry) => (
              <div key={entry.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <FileText size={16} style={{ color: "var(--ev-slate-400)" }} />
                  <span className="ev-body">{entry.name}</span>
                </div>
                <button className="ev-btn ev-btn-sm ev-btn-outline" onClick={() => void exportReport(entry)}>
                  <Download size={14} /> {entry.format.toUpperCase()}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
